import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CircularProgress } from "@mui/material";
import toast from "react-hot-toast";
import { useDispatch } from "react-redux";

import SoftBox from "../../../../components/SoftBox";
import SoftTypography from "../../../../components/SoftTypography";
import SoftButton from "../../../../components/SoftButton";
import SoftInput from "../../../../components/SoftInput";
import { GET_DASHBOARDS_FOR_USER } from "../../../../api/dashboard";
import {
  PIN_DASHBOARD_WIDGET,
  SET_HOME_DASHBOARDS,
  DELETE_WIDGET_FROM_LIST,
  GET_WIDGETS_LIST,
} from "../../../../api/userWidgets";
import { refetchUserData } from "../../../../redux/auth/actions";
import { UseIsAdmin, UseUserDetails, UseUserId } from "../../../../hooks/auth";
import { UseUsersDictionary } from "../../../../hooks/company";
import { clearDashboardAccessCache } from "../dashboardAccess";
import DashboardTile from "../DashboardTile";
import { HOME_DASHBOARD_LIMIT, pickHomeDashboards } from "../homeDashboards";

const DASHBOARD_WIDGET_KEY = "dashboard_link";

// The same magenta as the Dashboard widget's tile, so this modal reads as
// part of that card rather than as generic chrome.
const BRAND = "#B429C4";
const BRAND_SOFT = "rgba(180,41,196,0.10)";

const nameOf = (u) =>
  `${u?.firstName || ""} ${u?.lastName || ""}`.trim() || u?.email || "Player";

// Skips the trailing "(you)" marker so your own row still reads "KB".
const initialsOf = (name) =>
  (name || "")
    .trim()
    .split(" ")
    .filter((w) => w && w[0] !== "(")
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase() || "?";

const Initials = ({ name, active }) => (
  <span
    style={{
      flex: "none",
      width: 28,
      height: 28,
      borderRadius: "50%",
      display: "grid",
      placeItems: "center",
      fontSize: 11,
      fontWeight: 600,
      letterSpacing: "0.02em",
      color: active ? "#fff" : "#475467",
      background: active ? BRAND : "rgba(15,23,42,0.06)",
    }}
  >
    {initialsOf(name)}
  </span>
);

const Chevron = ({ open }) => (
  <svg
    width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#98A2B3"
    strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
    style={{ flex: "none", transform: open ? "rotate(180deg)" : "none", transition: "transform 140ms ease" }}
    aria-hidden="true"
  >
    <path d="m6 9 6 6 6-6" />
  </svg>
);

const Tick = () => (
  <svg
    width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={BRAND}
    strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"
    style={{ flex: "none" }} aria-hidden="true"
  >
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

const switchHint = (onHome, blocked) => {
  if (blocked) {
    return `The home page shows up to ${HOME_DASHBOARD_LIMIT} dashboards — turn one off to swap this in.`;
  }
  return onHome ? "Hide from the home page" : "Show on the home page";
};

const Cross = () => (
  <svg
    width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
    style={{ flex: "none" }} aria-hidden="true"
  >
    <path d="M18 6 6 18M6 6l12 12" />
  </svg>
);

// A small on/off switch in the house magenta. A real button with
// role="switch", so it's keyboard- and screen-reader-operable.
const HomeSwitch = ({ on, disabled, onChange, label, hint }) => (
  <button
    type="button"
    role="switch"
    aria-checked={on}
    aria-label={label}
    title={hint}
    disabled={disabled}
    onClick={onChange}
    style={{
      flex: "none",
      position: "relative",
      width: 34,
      height: 20,
      padding: 0,
      border: 0,
      borderRadius: 999,
      background: on ? BRAND : "#D0D5DD",
      opacity: disabled ? 0.45 : 1,
      cursor: disabled ? "not-allowed" : "pointer",
      transition: "background 140ms ease",
    }}
  >
    <span
      style={{
        position: "absolute",
        top: 2,
        left: on ? 16 : 2,
        width: 16,
        height: 16,
        borderRadius: "50%",
        background: "#fff",
        boxShadow: "0 1px 2px rgba(16,24,40,0.25)",
        transition: "left 140ms ease",
      }}
    />
  </button>
);

/**
 * Pick which Analytics boards land on someone's "My Widgets" panel.
 *
 * The board list is asked for PER PLAYER (`/dashboard/for-user/:id`) rather
 * than reused from the admin's own: an admin sees every "Company admins"
 * board and a rep sees none of them, so offering the admin's list would put
 * boards on screen that fail the moment they're saved. Switching player
 * reloads the list.
 */
const Dashboard = ({ widget, closeModal, setButtonLabel, setReFetch }) => {
  const dispatch = useDispatch();
  const myId = UseUserId();
  const me = UseUserDetails();
  const isAdmin = UseIsAdmin();
  const usersDict = UseUsersDictionary();

  const [targetId, setTargetId] = useState(String(myId || ""));
  const [boards, setBoards] = useState([]);
  const [picked, setPicked] = useState([]); // dashboard ids ticked right now
  const [home, setHome] = useState([]); // the picked ids that show on the home page
  const [pinned, setPinned] = useState([]); // the pins already saved
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [playerPickOpen, setPlayerPickOpen] = useState(false);
  const [playerQuery, setPlayerQuery] = useState("");
  const playerPickRef = useRef(null);
  useEffect(() => {
    if (!playerPickOpen) return undefined;
    const onDown = (e) => {
      if (playerPickRef.current && !playerPickRef.current.contains(e.target)) setPlayerPickOpen(false);
    };
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, [playerPickOpen]);

  const players = useMemo(() => {
    const rows = Object.entries(usersDict || {})
      // Archived accounts can't sign in, so a card pinned to one goes nowhere.
      .filter(([, u]) => !u?.isArchived)
      .map(([id, u]) => ({ id: String(id), name: nameOf(u) }))
      .filter((r) => r.id !== String(myId))
      .sort((a, b) => a.name.localeCompare(b.name));
    return [{ id: String(myId || ""), name: `${nameOf(me)} (you)` }, ...rows];
  }, [usersDict, myId, me]);

  const targetName = useMemo(
    () => players.find((p) => p.id === String(targetId))?.name || "Pick a player",
    [players, targetId],
  );

  const playersShown = useMemo(() => {
    const q = playerQuery.trim().toLowerCase();
    if (!q) return players;
    return players.filter((p) => p.name.toLowerCase().includes(q));
  }, [players, playerQuery]);

  const load = useCallback(async (who) => {
    if (!who) return;
    setLoading(true);
    setError("");
    try {
      const [boardsRes, pinsRes] = await Promise.all([
        GET_DASHBOARDS_FOR_USER(who),
        GET_WIDGETS_LIST({ key: DASHBOARD_WIDGET_KEY, userId: who, companyId: null }),
      ]);
      const rows = Array.isArray(boardsRes?.data) ? boardsRes.data : [];
      const pins = Array.isArray(pinsRes?.data) ? pinsRes.data : [];
      setBoards(rows);
      setPinned(pins);
      setPicked(pins.map((p) => String(p.dashboardId)).filter(Boolean));
      /* A pin that is no longer shared draws nothing, so it can't be holding
         one of the two home slots — work out the slots from readable boards. */
      const readable = new Set(rows.map((b) => String(b._id)));
      setHome(
        pickHomeDashboards(pins.filter((p) => readable.has(String(p.dashboardId))))
          .map((p) => String(p.dashboardId)),
      );
    } catch (err) {
      setBoards([]);
      setPinned([]);
      setPicked([]);
      setHome([]);
      setError(
        err?.response?.data?.error?.message
          || "Couldn't load that player's dashboards.",
      );
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load(targetId);
  }, [load, targetId]);

  /* Ticking a board assigns it, and it takes a home slot if one is free —
     so the first two you tick are the two that show, with no extra step.
     Unticking drops it from both. */
  const toggle = (id) => {
    if (picked.includes(id)) {
      setPicked(picked.filter((x) => x !== id));
      setHome(home.filter((x) => x !== id));
      return;
    }
    setPicked([...picked, id]);
    if (home.length < HOME_DASHBOARD_LIMIT) setHome([...home, id]);
  };

  const homeFull = home.length >= HOME_DASHBOARD_LIMIT;
  const toggleHome = (id) =>
    setHome((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      return prev.length < HOME_DASHBOARD_LIMIT ? [...prev, id] : prev;
    });

  // The assigned list, in the order they were assigned. Ids with no readable
  // board (access since revoked) are skipped — there's nothing to name them.
  const assigned = useMemo(
    () => picked.map((id) => boards.find((b) => String(b._id) === id)).filter(Boolean),
    [picked, boards],
  );

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return boards;
    return boards.filter((b) => (b.name || "").toLowerCase().includes(q));
  }, [boards, query]);

  const triggerSave = async () => {
    setSaving(true);
    try {
      const before = new Set(pinned.map((p) => String(p.dashboardId)).filter(Boolean));
      const after = new Set(picked);

      const toAdd = picked.filter((id) => !before.has(id));
      const toRemove = pinned.filter((p) => !after.has(String(p.dashboardId)));

      await Promise.all([
        ...toAdd.map((id) =>
          PIN_DASHBOARD_WIDGET({
            userId: targetId,
            dashboardId: id,
            title: boards.find((b) => String(b._id) === id)?.name || "Dashboard",
          }),
        ),
        ...toRemove.map((p) => DELETE_WIDGET_FROM_LIST(p._id, targetId)),
      ]);

      // After the adds, so the boards being placed exist by now.
      if (picked.length) {
        await SET_HOME_DASHBOARDS({ userId: targetId, dashboardIds: home });
      }

      clearDashboardAccessCache();
      if (String(targetId) === String(myId)) {
        dispatch(refetchUserData(myId));
        setButtonLabel(picked.length ? "Deactivate" : "Activate");
      }

      const mine = String(targetId) === String(myId);
      const who = mine
        ? "you"
        : (players.find((p) => p.id === String(targetId))?.name || "that player");
      const n = picked.length;
      toast.success(
        n
          ? `${n} dashboard${n === 1 ? "" : "s"} assigned to ${who} — ${home.length} on ${mine ? "your" : "their"} home page.`
          : `Dashboards removed from ${who}.`,
      );
      if (setReFetch) setReFetch(1);
      closeModal();
    } catch (err) {
      toast.error(
        err?.response?.data?.error?.message || "Couldn't save — please try again.",
      );
    }
    setSaving(false);
  };

  return (
    <>
      <SoftBox sx={{ marginTop: "1rem" }}>
        <SoftTypography variant="h2" sx={{ color: "#0F172A", fontSize: "22px", fontWeight: 600 }}>
          Dashboard Widget
        </SoftTypography>
        <SoftTypography
          variant="p"
          sx={{ color: "#67748E", fontSize: "14px", display: "block", marginTop: "4px" }}
        >
          Pick the Analytics dashboards to keep one click away. Up to{" "}
          {HOME_DASHBOARD_LIMIT} show as cards in My Widgets on the home page.
        </SoftTypography>
      </SoftBox>

      {isAdmin && (
        <SoftBox sx={{ marginTop: "1.25rem" }}>
          <SoftTypography
            sx={{ fontSize: "13px", fontWeight: 600, color: "#344054", marginBottom: "6px", display: "block" }}
          >
            Add to
          </SoftTypography>
          <SoftBox sx={{ position: "relative" }} ref={playerPickRef}>
            <SoftBox
              component="button"
              type="button"
              onClick={() => { setPlayerQuery(""); setPlayerPickOpen((v) => !v); }}
              sx={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                gap: "10px",
                height: 46,
                padding: "0 12px",
                borderRadius: "10px",
                border: "1px solid rgba(15,23,42,0.12)",
                background: "#fff",
                font: "inherit",
                cursor: "pointer",
                textAlign: "left",
                transition: "border-color 140ms ease, box-shadow 140ms ease",
                "&:hover": { borderColor: "rgba(15,23,42,0.22)" },
                ...(playerPickOpen && {
                  borderColor: BRAND,
                  boxShadow: `0 0 0 4px ${BRAND_SOFT}`,
                }),
              }}
            >
              <Initials name={targetName} />
              <SoftTypography
                sx={{ flex: 1, minWidth: 0, fontSize: "14px", fontWeight: 500, color: "#0F172A", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
              >
                {targetName}
              </SoftTypography>
              <Chevron open={playerPickOpen} />
            </SoftBox>

            {playerPickOpen && (
              <SoftBox
                sx={{
                  position: "absolute",
                  zIndex: 30,
                  top: "calc(100% + 6px)",
                  left: 0,
                  right: 0,
                  background: "#fff",
                  border: "1px solid rgba(15,23,42,0.10)",
                  borderRadius: "12px",
                  boxShadow: "0 16px 40px -10px rgba(16,24,40,0.22)",
                  padding: "6px",
                  overflow: "hidden",
                }}
              >
                <SoftBox
                  component="input"
                  // eslint-disable-next-line jsx-a11y/no-autofocus
                  autoFocus
                  type="text"
                  value={playerQuery}
                  placeholder="Search players"
                  onChange={(e) => setPlayerQuery(e.target.value)}
                  sx={{
                    width: "100%",
                    height: 38,
                    padding: "0 10px",
                    marginBottom: "4px",
                    borderRadius: "8px",
                    border: "1px solid rgba(15,23,42,0.12)",
                    font: "inherit",
                    fontSize: "13px",
                    color: "#0F172A",
                    outline: "none",
                    "&::placeholder": { color: "#98A2B3" },
                    "&:focus": { borderColor: BRAND, boxShadow: `0 0 0 3px ${BRAND_SOFT}` },
                  }}
                />
                <SoftBox sx={{ maxHeight: "232px", overflowY: "auto" }}>
                  {playersShown.map((p) => {
                    const on = p.id === String(targetId);
                    return (
                      <SoftBox
                        key={p.id}
                        component="button"
                        type="button"
                        onClick={() => { setTargetId(p.id); setPlayerPickOpen(false); }}
                        sx={{
                          width: "100%",
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                          border: 0,
                          borderRadius: "8px",
                          background: on ? BRAND_SOFT : "transparent",
                          padding: "8px 10px",
                          font: "inherit",
                          textAlign: "left",
                          cursor: "pointer",
                          "&:hover": { background: on ? BRAND_SOFT : "rgba(15,23,42,0.04)" },
                        }}
                      >
                        <Initials name={p.name} active={on} />
                        <SoftTypography
                          sx={{ flex: 1, minWidth: 0, fontSize: "13.5px", fontWeight: 500, color: on ? BRAND : "#0F172A", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                        >
                          {p.name}
                        </SoftTypography>
                        {on && <Tick />}
                      </SoftBox>
                    );
                  })}
                  {!playersShown.length && (
                    <SoftTypography sx={{ display: "block", padding: "12px 10px", fontSize: "13px", color: "#667085" }}>
                      No players match that.
                    </SoftTypography>
                  )}
                </SoftBox>
              </SoftBox>
            )}
          </SoftBox>
          <SoftTypography
            sx={{ fontSize: "12px", color: "#98A2B3", marginTop: "6px", display: "block" }}
          >
            Only dashboards this player can already open are listed. Share a
            dashboard with them first to see it here.
          </SoftTypography>
        </SoftBox>
      )}

      <SoftBox sx={{ marginTop: "1.25rem" }}>
        <SoftBox sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px", marginBottom: "6px" }}>
          <SoftTypography sx={{ fontSize: "13px", fontWeight: 600, color: "#344054" }}>
            Assigned dashboards
          </SoftTypography>
          {!loading && !error && (
            <SoftBox
              component="span"
              sx={{
                flex: "none",
                fontSize: "12px",
                fontWeight: 600,
                lineHeight: 1,
                padding: "5px 9px",
                borderRadius: "999px",
                color: home.length ? BRAND : "#667085",
                background: home.length ? BRAND_SOFT : "rgba(15,23,42,0.05)",
              }}
            >
              {home.length} of {HOME_DASHBOARD_LIMIT} on home page
            </SoftBox>
          )}
        </SoftBox>

        <SoftBox
          sx={{
            border: "1px solid rgba(15,23,42,0.10)",
            borderRadius: "12px",
            background: "rgba(255,255,255,0.6)",
            maxHeight: "196px",
            overflowY: "auto",
          }}
        >
          {loading && (
            <SoftBox sx={{ display: "flex", justifyContent: "center", padding: "18px" }}>
              <CircularProgress size={20} />
            </SoftBox>
          )}

          {!loading && !assigned.length && (
            <SoftTypography sx={{ display: "block", padding: "16px 14px", fontSize: "13px", color: "#667085" }}>
              {error ? "Nothing to show." : "Nothing assigned yet. Tick a dashboard below to add it."}
            </SoftTypography>
          )}

          {!loading && assigned.map((b) => {
            const id = String(b._id);
            const onHome = home.includes(id);
            const blocked = !onHome && homeFull;
            const count = (b.widgets || []).length;
            return (
              <SoftBox
                key={id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  padding: "10px 12px 10px 14px",
                  borderBottom: "1px solid rgba(15,23,42,0.06)",
                  "&:last-of-type": { borderBottom: 0 },
                }}
              >
                <DashboardTile size={34} radius={10} />
                <SoftBox sx={{ minWidth: 0, flex: 1 }}>
                  <SoftTypography
                    sx={{
                      display: "block",
                      fontSize: "14px",
                      fontWeight: 500,
                      color: "#0F172A",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {b.name || "Untitled dashboard"}
                  </SoftTypography>
                  <SoftTypography sx={{ display: "block", fontSize: "12px", color: onHome ? BRAND : "#98A2B3", fontWeight: onHome ? 600 : 400 }}>
                    {onHome ? "Showing on home page" : `${count} widget${count === 1 ? "" : "s"} · not on home page`}
                  </SoftTypography>
                </SoftBox>
                <HomeSwitch
                  on={onHome}
                  disabled={blocked}
                  onChange={() => toggleHome(id)}
                  label={`Show ${b.name || "this dashboard"} on the home page`}
                  hint={switchHint(onHome, blocked)}
                />
                <SoftBox
                  component="button"
                  type="button"
                  aria-label={`Remove ${b.name || "dashboard"}`}
                  title="Remove"
                  onClick={() => toggle(id)}
                  sx={{
                    flex: "none",
                    display: "grid",
                    placeItems: "center",
                    width: 28,
                    height: 28,
                    border: 0,
                    borderRadius: "8px",
                    background: "transparent",
                    color: "#98A2B3",
                    cursor: "pointer",
                    "&:hover": { background: "rgba(15,23,42,0.06)", color: "#475467" },
                  }}
                >
                  <Cross />
                </SoftBox>
              </SoftBox>
            );
          })}
        </SoftBox>

        {homeFull && (
          <SoftTypography sx={{ fontSize: "12px", color: "#98A2B3", marginTop: "6px", display: "block" }}>
            The home page is full. Turn one off to swap another in.
          </SoftTypography>
        )}
      </SoftBox>

      <SoftBox sx={{ marginTop: "1.25rem" }}>
        <SoftInput
          placeholder="Search dashboards"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </SoftBox>

      <SoftBox
        sx={{
          marginTop: "10px",
          maxHeight: "280px",
          overflowY: "auto",
          border: "1px solid rgba(15,23,42,0.10)",
          borderRadius: "12px",
          background: "rgba(255,255,255,0.6)",
        }}
      >
        {loading && (
          <SoftBox sx={{ display: "flex", justifyContent: "center", padding: "28px" }}>
            <CircularProgress size={22} />
          </SoftBox>
        )}

        {!loading && !!error && (
          <SoftTypography sx={{ display: "block", padding: "20px", fontSize: "13px", color: "#B42318" }}>
            {error}
          </SoftTypography>
        )}

        {!loading && !error && !shown.length && (
          <SoftTypography sx={{ display: "block", padding: "20px", fontSize: "13px", color: "#667085" }}>
            {boards.length
              ? "No dashboards match that search."
              : "No dashboards to pin yet."}
          </SoftTypography>
        )}

        {!loading && !error && shown.map((b) => {
          const id = String(b._id);
          const on = picked.includes(id);
          const count = (b.widgets || []).length;
          return (
            <SoftBox
              key={id}
              component="button"
              type="button"
              role="checkbox"
              aria-checked={on}
              onClick={() => toggle(id)}
              sx={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                gap: "10px",
                textAlign: "left",
                border: 0,
                borderBottom: "1px solid rgba(15,23,42,0.06)",
                background: on ? "rgba(180,41,196,0.06)" : "transparent",
                padding: "11px 14px",
                cursor: "pointer",
                font: "inherit",
                "&:last-of-type": { borderBottom: 0 },
                "&:hover": { background: on ? "rgba(180,41,196,0.10)" : "rgba(15,23,42,0.04)" },
              }}
            >
              <input
                type="checkbox"
                checked={on}
                readOnly
                tabIndex={-1}
                style={{ accentColor: "#B429C4", width: 16, height: 16, flex: "none" }}
              />
              <SoftBox sx={{ minWidth: 0, flex: 1 }}>
                <SoftTypography
                  sx={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "#0F172A",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {b.name || "Untitled dashboard"}
                </SoftTypography>
                <SoftTypography sx={{ display: "block", fontSize: "12px", color: "#98A2B3" }}>
                  {`${count} widget${count === 1 ? "" : "s"}`}
                </SoftTypography>
              </SoftBox>
              {home.includes(id) && (
                <SoftBox
                  component="span"
                  sx={{
                    flex: "none",
                    fontSize: "11px",
                    fontWeight: 600,
                    lineHeight: 1,
                    padding: "4px 8px",
                    borderRadius: "999px",
                    color: BRAND,
                    background: BRAND_SOFT,
                  }}
                >
                  On home
                </SoftBox>
              )}
            </SoftBox>
          );
        })}
      </SoftBox>

      <SoftBox
        display="flex"
        justifyContent="center"
        alignItems="center"
        gap="1rem"
        sx={{ marginTop: "1.5rem" }}
      >
        <SoftButton
          onClick={closeModal}
          disabled={saving}
          sx={{
            width: "100%",
            textTransform: "none",
            borderRadius: "4px",
            border: "1px solid rgba(15,23,42,0.12)",
            boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.05)",
            background: "rgba(15,23,42,0.03)",
            color: "#475467",
            "&:hover": { background: "rgba(15,23,42,0.06)" },
          }}
        >
          Cancel
        </SoftButton>
        <SoftButton
          color="primary"
          type="submit"
          sx={{ width: "100%", textTransform: "none" }}
          onClick={triggerSave}
          disabled={saving || loading || !!error}
          loading={saving}
        >
          Save
        </SoftButton>
      </SoftBox>
    </>
  );
};

export default Dashboard;
